import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import { notificationsAPI } from '../services/api';
import {
  Bell,
  Calendar,
  Users,
  MessageSquare,
  Filter,
  Search,
  TrendingUp,
  CheckCircle,
  Clock
} from 'lucide-react';

const NotificationsHistory = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('فشل في تحميل سجل الإشعارات');
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      prayer: 'صلاة',
      event: 'حدث',
      announcement: 'إعلان',
      booking: 'حجز'
    };
    return types[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      prayer: 'bg-green-100 text-green-800',
      event: 'bg-blue-100 text-blue-800',
      announcement: 'bg-purple-100 text-purple-800',
      booking: 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getTypeIcon = (type) => {
    const icons = {
      prayer: <Bell className="w-4 h-4" />,
      event: <Calendar className="w-4 h-4" />,
      announcement: <MessageSquare className="w-4 h-4" />,
      booking: <CheckCircle className="w-4 h-4" />
    };
    return icons[type] || <Bell className="w-4 h-4" />;
  };

  const filterByDate = (notification) => {
    if (dateFilter === 'all') return true;

    const notificationDate = new Date(notification.createdAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
      case 'today':
        return notificationDate >= today;
      case 'week': {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return notificationDate >= weekAgo;
      }
      case 'month': {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return notificationDate >= monthAgo;
      }
      default:
        return true;
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch =
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || notification.type === filterType;
    const matchesDate = filterByDate(notification);

    return matchesSearch && matchesType && matchesDate;
  });

  const stats = {
    total: notifications.length,
    types: {
      prayer: notifications.filter(n => n.type === 'prayer').length,
      event: notifications.filter(n => n.type === 'event').length,
      announcement: notifications.filter(n => n.type === 'announcement').length,
      booking: notifications.filter(n => n.type === 'booking').length,
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary">سجل الإشعارات</h1>
            <p className="text-text-secondary">عرض جميع الإشعارات المرسلة</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">إجمالي الإشعارات</p>
                <p className="text-2xl font-bold text-text-primary mt-2">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Bell className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">الصلاة</p>
                <p className="text-2xl font-bold text-green-600 mt-2">{stats.types.prayer}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Bell className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">الأحداث</p>
                <p className="text-2xl font-bold text-blue-600 mt-2">{stats.types.event}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-6 border border-divider">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-text-secondary text-sm font-medium">اليوم</p>
                <p className="text-2xl font-bold text-accent-gold mt-2">
                  {notifications.filter(n => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return new Date(n.createdAt) >= today;
                  }).length}
                </p>
              </div>
              <div className="bg-yellow-100 p-3 rounded-lg">
                <TrendingUp className="h-6 w-6 text-accent-gold" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-divider p-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted h-5 w-5" />
              <input
                type="text"
                placeholder="البحث في الإشعارات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 px-4 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع الأنواع</option>
              <option value="prayer">صلاة</option>
              <option value="event">حدث</option>
              <option value="announcement">إعلان</option>
              <option value="booking">حجز</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="all">جميع الفترات</option>
              <option value="today">اليوم</option>
              <option value="week">هذا الأسبوع</option>
              <option value="month">هذا الشهر</option>
            </select>
          </div>

          <div className="space-y-4">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-primary-light rounded-lg p-4 border border-divider hover:border-accent-gold transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 space-x-reverse flex-1">
                    <div className={`p-3 rounded-lg ${getTypeColor(notification.type).replace('text-', 'text-opacity-100 ')}`}>
                      {getTypeIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-text-primary">
                          {notification.title}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(notification.type)}`}>
                          {getTypeLabel(notification.type)}
                        </span>
                      </div>

                      <p className="text-text-secondary mb-3">
                        {notification.message}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-text-muted">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 ml-1" />
                          {new Date(notification.createdAt).toLocaleDateString('ar-SA')}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 ml-1" />
                          {new Date(notification.createdAt).toLocaleTimeString('ar-SA', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        {notification.user && (
                          <div className="flex items-center">
                            <Users className="w-4 h-4 ml-1" />
                            {notification.user.name}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredNotifications.length === 0 && (
            <div className="text-center py-12">
              <Bell className="mx-auto h-12 w-12 text-text-muted mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">لا توجد إشعارات</h3>
              <p className="text-text-muted">لا توجد إشعارات مطابقة لمعايير البحث</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card rounded-xl border border-divider p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <Filter className="w-5 h-5 ml-2 text-accent-gold" />
              الإشعارات حسب النوع
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.types).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-primary-light rounded-lg">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className={`p-2 rounded-lg ${getTypeColor(type).replace('text-', 'text-opacity-100 ')}`}>
                      {getTypeIcon(type)}
                    </div>
                    <span className="text-text-primary font-medium">{getTypeLabel(type)}</span>
                  </div>
                  <span className="text-2xl font-bold text-accent-gold">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-xl border border-divider p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 ml-2 text-accent-gold" />
              النشاط الأخير
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">آخر إشعار</p>
                    <p className="text-xs text-green-600">
                      {notifications[0] ? new Date(notifications[0].createdAt).toLocaleString('ar-SA') : 'لا يوجد'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <Users className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">متوسط الإشعارات اليومية</p>
                    <p className="text-xs text-blue-600">
                      {Math.round(notifications.length / 30)} إشعار
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <MessageSquare className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="text-sm font-medium text-purple-800">أكثر نوع إشعار</p>
                    <p className="text-xs text-purple-600">
                      {Object.entries(stats.types).sort((a, b) => b[1] - a[1])[0]?.[0]
                        ? getTypeLabel(Object.entries(stats.types).sort((a, b) => b[1] - a[1])[0][0])
                        : 'لا يوجد'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default NotificationsHistory;
